import { fetchAuthSession } from "aws-amplify/auth";
import runtimeConfig from "runtimeConfig";
import { TextTrans } from "utils/constants";
import { endExpiredSession } from "utils";

// The new API (backend/, spec backend/openapi.json). Every call carries the
// Cognito ID token; errors come back as { error: { code, message } }.

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code);
    this.status = status;
    this.code = code;
  }
}

// Japanese text for an error: business errors already carry it in `message`;
// generic codes and the remaining English messages go through TextTrans.
export const errorText = (error) =>
  TextTrans[error?.code] || TextTrans[error?.message] || error?.message || "";

async function idToken(forceRefresh) {
  let session;
  try {
    session = await fetchAuthSession({ forceRefresh });
  } catch (error) {
    console.log("no auth session: ", error);
  }
  const token = session?.tokens?.idToken?.toString();
  if (!token) await endExpiredSession();
  return token;
}

function urlOf(path, query) {
  const url = new URL(`${runtimeConfig().apiBaseUrl}${path}`, window.location.origin);
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    // arrays go as repeated keys: ?type=spUser&type=NTTSpUser
    [].concat(value).forEach((v) => url.searchParams.append(key, v));
  });
  return url.toString();
}

async function errorOf(res) {
  const body = await res.json().catch(() => null);
  const { code, message } = body?.error ?? {};
  return new ApiError(res.status, code || `HTTP_${res.status}`, message);
}

/**
 * One request: JSON in, JSON out (`text: true` for CSV). Retries once with a
 * refreshed token on 401. Throws ApiError on any non-2xx.
 */
export async function api(method, path, { query, body, text = false } = {}) {
  const send = async (forceRefresh) =>
    fetch(urlOf(path, query), {
      method,
      headers: {
        Authorization: `Bearer ${await idToken(forceRefresh)}`,
        ...(body !== undefined && { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res = await send(false);
  if (res.status === 401) res = await send(true);
  if (!res.ok) {
    const error = await errorOf(res);
    // the group requires MFA and this user has not set TOTP up: signing in
    // again leads to the setup screen
    if (error.code === "MFA_SETUP_REQUIRED") await endExpiredSession(errorText(error));
    throw error;
  }
  if (res.status === 204) return null;
  return text ? res.text() : res.json();
}

/**
 * The `{ data, statusCode }` shape the modals check: any 2xx is reported as
 * 200 (the callers predate 201 / 204); on failure `data` is the Japanese
 * message and `code` the API's error code.
 */
export async function asResult(promise) {
  try {
    return { data: await promise, statusCode: 200 };
  } catch (error) {
    return { data: errorText(error), statusCode: error.status, code: error.code };
  }
}

/** Saves CSV text as a file, with a BOM so Excel reads it as UTF-8. */
export function downloadCsv(csv, filename) {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}
